import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText'];

  get currentCartCount() {
    return parseInt(this.refs.cartBubbleText.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleText.textContent = value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);

    // Fast paint (optional)
    this.ensureCartBubbleIsCorrect();

    // Always reconcile with real cart state
    this.reconcileCartCount();

    // Mobile Safari BFCache restore fix
    window.addEventListener('pageshow', this.#onPageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
  }

  #onPageShow = (e) => {
    // When user navigates back/forward on iOS, page can be restored from memory
    if (e.persisted) this.reconcileCartCount();
  };

  reconcileCartCount = () => {
    fetch(`${Theme.routes.root}cart.js`, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
      .then((r) => r.json())
      .then((cart) => {
        // IMPORTANT: dispatch a cartUpdate so *all* cart-icon instances update (desktop + mobile)
        document.dispatchEvent(
          new CartUpdateEvent(cart, 'cart-icon:reconcile', {
            itemCount: cart.item_count,
            source: 'cart-icon:reconcile',
          })
        );
      })
      .catch(() => {});
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm
      ? this.currentCartCount + itemCount
      : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', this.currentCartCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);
    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct (sessionStorage helper).
   */
  ensureCartBubbleIsCorrect = () => {
    const raw = sessionStorage.getItem('cart-count');
    if (!raw) return;

    try {
      const { value, timestamp } = JSON.parse(raw);
      const visibleCount = this.refs.cartBubbleText.textContent ?? '0';

      // If already correct, do nothing
      if (String(value) === String(visibleCount)) return;

      // Use a longer window than 10s (mobile navigation can exceed 10s easily)
      if (Date.now() - timestamp < 10 * 60 * 1000) {
        const count = parseInt(value, 10);
        if (count >= 0) this.renderCartBubble(count, false, false);
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
